import { AriaAttributes, CSSProperties, useId, useMemo } from 'react';
import classNames from 'classnames';

import { InputErrorProps, InputSize, parseWidthCssVar } from '@xemplo/input-utils';

import { TextAreaProps, TextareaResize } from './text-area.types';

function isLabelHidden(label?: string) {
  if (!label) return true;
  return label.trim() === '';
}

function getEffectivePlaceholder(isHidden: boolean, placeholder?: string) {
  if (!isHidden) return undefined;
  return placeholder;
}

export function useTextareaMeta(props: TextAreaProps) {
  const {
    id,
    name,
    label,
    width,
    errorMessage,
    description,
    placeholder,
    leadingIcon,
    trailingIcon,
    className,
    disabled,
    readOnly,
    wrapperId,
    stretch = false,
    maxHeight = '200px',
    resize = TextareaResize.none,
    rows = 3,
    style,
    value,
    ...restProps
  } = props;

  const internalId = useId();
  const implementationId = id || name;
  const inputId = `${internalId}${implementationId}`;

  const hasValue = !!value;
  const hasError = !!errorMessage;
  const isHiddenLabel = useMemo(() => isLabelHidden(label), [label]);

  const errorId = useMemo<string | undefined>(() => {
    if (!hasError) return undefined;
    return `${inputId}-error`;
  }, [hasError, inputId]);

  const descriptionId = useMemo<string | undefined>(() => {
    if (!description) return undefined;
    return `${inputId}-description`;
  }, [description, inputId]);

  const ariaDescribedBy = useMemo<string | undefined>(() => {
    if (hasError) return errorId;
    return descriptionId;
  }, [hasError, errorId, descriptionId]);

  const effectivePlaceholder = useMemo<string | undefined>(
    () => getEffectivePlaceholder(isHiddenLabel, placeholder),
    [isHiddenLabel, placeholder],
  );

  const ariaProps = useMemo<Partial<AriaAttributes>>(
    () => ({
      ...(hasError && { 'aria-invalid': true }),
      ...(disabled && { 'aria-disabled': true }),
      ...(readOnly && { 'aria-readonly': true }),
      ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
    }),
    [hasError, disabled, readOnly, ariaDescribedBy],
  );

  const maybeErrorOrDescriptionProps = useMemo<InputErrorProps>(
    () => ({
      description,
      descriptionProps: descriptionId ? { id: descriptionId } : undefined,
      error: hasError,
      errorMessage,
      errorMessageProps: errorId ? { id: errorId } : undefined,
      testId: inputId,
    }),
    [description, descriptionId, hasError, errorMessage, errorId, inputId],
  );

  const wrapperStyle = useMemo<CSSProperties>(
    () => ({
      ...parseWidthCssVar(width),
      ...style,
    }),
    [width, style],
  );

  const textareaStyle = useMemo<CSSProperties>(
    () => ({ maxHeight, resize }),
    [maxHeight, resize],
  );

  const containerClassName = useMemo<string>(
    () =>
      classNames('xemplo-textarea-container', InputSize.Default, className, {
        disabled,
        readOnly,
        invalid: hasError,
        'has-value': hasValue,
      }),
    [className, disabled, readOnly, hasError, hasValue],
  );

  const textareaClassName = useMemo<string>(
    () =>
      classNames(className, {
        'has-value': hasValue,
        'has-placeholder': !!effectivePlaceholder,
        'has-label': !isHiddenLabel,
        'label-hidden': isHiddenLabel,
      }),
    [className, hasValue, effectivePlaceholder, isHiddenLabel],
  );

  const labelClassName = useMemo<string>(
    () =>
      classNames(InputSize.Default, {
        hidden: isHiddenLabel,
        left: !!leadingIcon,
      }),
    [isHiddenLabel, leadingIcon],
  );

  const baseTextareaProps = useMemo(
    () => ({
      ...restProps,
      ...ariaProps,
      id: inputId,
      name,
      disabled,
      readOnly,
      placeholder: effectivePlaceholder,
      rows,
      value,
    }),
    [
      restProps,
      ariaProps,
      inputId,
      name,
      disabled,
      readOnly,
      effectivePlaceholder,
      rows,
      value,
    ],
  );

  return {
    wrapperId,
    inputId,
    isHiddenLabel,
    hasError,
    wrapperStyle,
    textareaStyle,
    containerClassName,
    textareaClassName,
    labelClassName,
    maybeErrorOrDescriptionProps,
    baseTextareaProps,
    label,
    leadingIcon,
    trailingIcon,
    stretch,
    resize,
  };
}
